import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import NavigationAccountTrigger from 'frontend-payments/components/navigation/account-trigger';
import NavigationTabsTabNavigation from 'frontend-payments/components/navigation/tabs/tab-navigation';
import type { AccountListItem } from 'frontend-payments/models';
import { Illustration } from 'frontend-utils';
import {
  LANGUAGE_LABELS,
  LANGUAGES,
} from 'frontend-utils/utils/constants/constant-values';
import {
  getDayjsLocale,
  getTranslationLocale,
  setLanguagePreference,
} from 'frontend-utils/utils/language-preference';

import styles from './header.module.css';

export type MenuItem = {
  active: boolean | undefined;
  label: string;
  route: string;
};

const SETTINGS_ACTIONS = {
  CHANGE_LANGUAGE: 'changeLanguage',
  LOGOUT: 'logout',
};

type SettingsActionType = keyof typeof SETTINGS_ACTIONS;

type SelectInputOption = Option & {
  action: SettingsActionType;
  id?: string;
};

type AccountOption = Option & {
  id: string;
};

type GroupedAccountOption = {
  groupName: string;
  id?: string;
  options: AccountOption[];
};

interface NavigationHeaderSignature {
  Args: {
    isAccountSelectorDisabled: boolean;
  };
}

export default class NavigationHeader extends Component<NavigationHeaderSignature> {
  @service declare accountSwitcher: Services['account-switcher'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare session: Services['session'];

  @tracked accountActive = false;

  @cached
  get accountOptions(): GroupedAccountOption[] {
    const accountOptions = this.accountSwitcher.availableAccounts.map(
      (account: AccountListItem) => ({
        id: account.accountNumber,
        label: account.name,
        value: account.accountNumber,
        unsafe_subLabel: account.accountNumber,
        hasOverdueInvoices: account.hasOverdueInvoices,
      }),
    );

    const accountsWithOverdue = accountOptions.filter(
      (option) => option.hasOverdueInvoices,
    );
    const accountsWithoutOverdue = accountOptions.filter(
      (option) => !option.hasOverdueInvoices,
    );

    const groupedOptions = [];

    if (accountsWithOverdue.length > 0) {
      groupedOptions.push({
        groupName: this.intl.t('payments.accounts.with_overdue_invoices'),
        options: accountsWithOverdue,
      });
    }

    if (accountsWithoutOverdue.length > 0) {
      groupedOptions.push({
        groupName: this.intl.t('payments.accounts.all_accounts'),
        options: accountsWithoutOverdue,
      });
    }

    return groupedOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentAccountName() {
    const currentAccount = this.accountSwitcher.currentAccount;
    return currentAccount?.name || '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentLanguageLabel() {
    const currentLocale = this.intl.primaryLocale;
    return (
      LANGUAGE_LABELS[currentLocale as keyof typeof LANGUAGE_LABELS] ||
      LANGUAGE_LABELS[LANGUAGES.EN]
    );
  }

  get hasMultipleAccounts(): boolean {
    return this.accountSwitcher.hasMultipleAccounts;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBillingAdminsActive() {
    return this.router.currentRouteName?.includes('billing-admins');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOverviewActive() {
    return this.router.currentRouteName?.includes('overview');
  }

  get menuItems(): MenuItem[] {
    return [
      {
        label: this.intl.t('payments.app.header.overview'),
        route: 'authenticated.overview',
        active: this.isOverviewActive,
      },
      {
        label: this.intl.t('payments.app.header.billing_admins'),
        route: 'authenticated.billing-admins',
        active: this.isBillingAdminsActive,
      },
    ];
  }

  get selectedAccount(): AccountOption | null {
    const currentAccountId = this.accountSwitcher.currentAccountId;
    if (!currentAccountId) {
      return null;
    }

    const allOptions = this.accountOptions.flatMap((group) => group.options);
    return allOptions.find((option) => option.id === currentAccountId) ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get settingsOptions() {
    return [
      // Add language switcher back in the future, everything should work already
      // {
      //   groupName: this.intl.t('payments.menu.language', {
      //     language: this.currentLanguageLabel,
      //   }),
      //   label: this.currentLanguageLabel,
      //   unsafe_isCollapsed: true,
      //   icon: 'events-global',
      //   options: LANGUAGES_LIST.map((language) => ({
      //     id: language.id,
      //     label: language.label,
      //     action: SETTINGS_ACTIONS.CHANGE_LANGUAGE,
      //   })),
      // },
      {
        label: this.intl.t('payments.menu.log_out'),
        unsafe_subLabel: this.userEmail,
        action: SETTINGS_ACTIONS.LOGOUT,
        icon: 'logout' as const,
      },
    ];
  }

  get shouldShowAccountSearch(): boolean {
    const totalAccounts = this.accountOptions.reduce(
      (total, group) => total + group.options.length,
      0,
    );
    return totalAccounts > 5;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get userEmail() {
    return this.session.user?.email;
  }

  @action
  accountSearchMatcher(
    item: GroupedAccountOption | AccountOption,
    searchText: string,
  ): number {
    // If it's a group, search within its options
    if ('options' in item && Array.isArray(item.options)) {
      return item.options.some((option: AccountOption) =>
        option.label.toLowerCase().includes(searchText.toLowerCase()),
      )
        ? 0
        : -1;
    }

    // If it's a direct account option
    if ('label' in item && typeof item.label === 'string') {
      return item.label.toLowerCase().indexOf(searchText.toLowerCase());
    }

    return -1;
  }

  @action
  onAccountChange(option: AccountOption): void {
    try {
      // Switch to the new account, which will reload all data
      this.accountSwitcher.switchAccount(option.id);

      // If we're on the payment-method route, redirect to overview
      // since the payment details are specific to the previous account
      if (this.router.currentRouteName === 'authenticated.payment-method') {
        this.router.transitionTo('authenticated.overview');
      }

      // Trigger a route refresh to reload all dependent data
      this.router.refresh();
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  @action
  performAccountAction(option: SelectInputOption): void {
    if (option.action === SETTINGS_ACTIONS.CHANGE_LANGUAGE && option.id) {
      setLanguagePreference(option.id);
      const translationLocale = getTranslationLocale(option.id);
      const dayjsLocale = getDayjsLocale(option.id);

      // Set intl locale for translations
      this.intl.setLocale(translationLocale);

      // Set dayjs locale for date formatting
      dayjs.locale(dayjsLocale);
    } else if (option.action === SETTINGS_ACTIONS.LOGOUT) {
      void this.session.invalidate();
    }
  }

  @action
  toggleAccountActive(): void {
    this.accountActive = !this.accountActive;
  }

  <template>
    <header class={{styles.container}}>
      <div class={{styles.content-wrapper}}>
        <div class="flex w-full items-center justify-between">
          <Illustration
            @html={{ICONS_LOGOS_MAP.logo-color}}
            class={{styles.platform-logo}}
          />

          <ul class={{styles.navigation-list}}>
            {{#each this.menuItems as |item|}}
              <NavigationTabsTabNavigation @item={{item}} />
            {{/each}}
          </ul>

          <div class="flex items-center gap-500">
            {{#if this.hasMultipleAccounts}}
              <div>
                <PrismComboSelectInput
                  @disabled={{@isAccountSelectorDisabled}}
                  @horizontalPosition="right"
                  @isSearchable={{if this.shouldShowAccountSearch true}}
                  @onChange={{this.onAccountChange}}
                  @options={{this.accountOptions}}
                  @searchMatcher={{this.accountSearchMatcher}}
                  @value={{this.selectedAccount}}
                />
              </div>
            {{else}}
              <span class="text-300--medium">{{this.currentAccountName}}</span>
            {{/if}}
            <div
              class={{concat styles.account-button " " "flex justify-center"}}
            >
              <PrismComboSelectInput
                @horizontalPosition="right"
                @onChange={{this.performAccountAction}}
                @onClose={{this.toggleAccountActive}}
                @onOpen={{this.toggleAccountActive}}
                @options={{this.settingsOptions}}
                @unsafe_collapsibleGroupBackLabel={{t "general.back"}}
                @unsafe_dropdownClass={{styles.account-button-dropdown}}
                @unsafe_triggerComponent={{NavigationAccountTrigger}}
                @value={{null}}
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  </template>
}
